<script setup>
/* Name, Kurzbeschreibung und Status eines Spielers — der Teil einer Zeile,
   der in PlayerRow.vue und TransferredRow.vue identisch ist. Eigene
   Komponente statt Kopie, weil hier nicht nur Rechenlogik doppelt wäre,
   sondern auch die Markup-/CSS-Struktur (.name, .name-first, .name-last,
   .desc) — genau der Fall aus CLAUDE.md, Abschnitt "Reuse-first".
   Kein eigenes Wurzelelement: Vue 3 erlaubt mehrere Wurzelknoten, dadurch
   bleiben .row .name, .row .desc und .row .status-line ansprechbar. */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import StatusChip from './StatusChip.vue';
import AgeGroupChip from './AgeGroupChip.vue';
import { ageGroupOf } from '../domain/ageGroups.js';
import { positionAbbr } from '../domain/positions.js';
import { birthYearOf } from '../domain/birthYear.js';

const props = defineProps({
  player: { type: Object, required: true },
  /* Auf der Seite AFH Transfers sind alle Spieler transferiert, das Badge sagt dort
     nichts — TransferredRow schaltet es ab. */
  showStatus: { type: Boolean, default: true },
});

const { t } = useI18n();

/* Natürliche Reihenfolge, Vorname zurückgenommen, Nachname kräftig —
   Entscheidung Bastian, 2026-09-17. Kein Vor-/Nachname vorhanden: Bindestrich
   statt leerer Zeile. */
const firstName = computed(() => props.player.firstName || '');
const lastName = computed(() => props.player.lastName || '');
const hasName = computed(() => !!(firstName.value || lastName.value));
/* Ohne Nachnamen traegt der Vorname die Betonung, statt in --muted zu
   verschwinden — sonst wirkt die einzige vorhandene Namensangabe wie ein
   Nebensatz. */
const emphasized = computed(() => lastName.value || firstName.value);
const dimmed = computed(() => (lastName.value ? firstName.value : ''));

/* Kürzel statt ausgeschriebener Position — bleibt englisch (siehe
   domain/positions.js), nur "Position offen" ist übersetzt. */
const posLabel = computed(() =>
  props.player.pos ? positionAbbr(props.player.pos) : t('player.positionTbd')
);

/* Rückennummer bewusst nicht angezeigt (im Jugendkader nicht durchgängig
   vergeben). Geburtsjahr statt Alter, mit Strich statt NaN, wenn keins
   hinterlegt ist. */
const yearLabel = computed(() => t('player.born', { year: birthYearOf(props.player) || '—' }));
/* Altersklasse: kleiner Chip neben dem Status-Badge (gleiche Zeile, kein zusätzlicher Platz); ohne Wert nichts */
</script>

<template>
  <!-- Drei feste Zeilen von oben nach unten: Name, Position · Geburtsjahr,
       Status-Badge (Entscheidung Bastian 2026-10-04). Jede Zeile steht für sich,
       die Reihenfolge hängt nicht von der Länge des Namens ab. Nur die Listenkarten
       nutzen diese Komponente; der Panel-Kopf hat eigenes Markup (Chip neben dem Namen). -->
  <span class="name-line">
    <span class="name">
      <template v-if="hasName">
        <span v-if="dimmed" class="name-first">{{ dimmed }}</span>
        <span class="name-last">{{ emphasized }}</span>
      </template>
      <template v-else>—</template>
    </span>
  </span>
  <span class="desc">{{ posLabel }} · {{ yearLabel }}</span>
  <span v-if="showStatus" class="status-line">
    <StatusChip :status="player.status" />
    <AgeGroupChip v-if="ageGroupOf(player)" small :value="ageGroupOf(player)" />
  </span>
</template>

<style scoped>
/* Status-Chip und Altersklasse in einer Zeile */
.status-line {
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}
</style>
